/**
 * @fileoverview Display helpers for rate cards, rate timelines and the
 * financial-year rate rollover (MOR-1018, MOR-1038).
 *
 * Pure by design: no React, no Convex. The money arithmetic itself lives in
 * `convex/timesheets/rateMath.ts`; this file only turns it into words.
 */

import { format, parseISO } from "date-fns";

/** "25% discount", "25% uplift", or "Same as Standard" for a multiplier of 1. */
export function describeMultiplier(multiplier: number): string {
  const percent = Math.round(Math.abs(multiplier - 1) * 1000) / 10;
  if (percent === 0) return "Same as Standard";
  return `${percent}% ${multiplier < 1 ? "discount" : "uplift"}`;
}

/** "nearest $10" / "nearest cent". */
export function describeRounding(roundTo: number | undefined): string {
  return roundTo !== undefined && roundTo > 0
    ? `nearest $${roundTo.toLocaleString("en-AU")}`
    : "nearest cent";
}

export function formatRate(hourlyRate: number): string {
  return `$${hourlyRate.toLocaleString("en-AU", {
    minimumFractionDigits: Number.isInteger(hourlyRate) ? 0 : 2,
    maximumFractionDigits: 2,
  })}/hr`;
}

/** "30 Jun 2024" for an ISO date. */
export function formatIsoDate(iso: string): string {
  return format(parseISO(iso), "d MMM yyyy");
}

export type RatePeriodState = "past" | "current" | "upcoming";

export interface RatePeriod<T> {
  rate: T;
  from: string;
  /** Inclusive last day this rate applies; absent while it is open-ended. */
  to?: string;
  state: RatePeriodState;
}

function dayBefore(iso: string): string {
  const date = parseISO(iso);
  date.setDate(date.getDate() - 1);
  return format(date, "yyyy-MM-dd");
}

/**
 * A grade's dated rates as periods with an end, newest first, so the settings
 * screen can say "which rates apply from when" rather than leaving the reader
 * to work it out from a list of start dates.
 */
export function rateTimeline<T extends { effectiveFrom: string }>(
  rates: readonly T[],
  today: string,
): RatePeriod<T>[] {
  const ascending = [...rates].sort((a, b) =>
    a.effectiveFrom.localeCompare(b.effectiveFrom),
  );
  const currentIndex = ascending.reduce(
    (found, rate, i) => (rate.effectiveFrom <= today ? i : found),
    -1,
  );
  return ascending
    .map((rate, i): RatePeriod<T> => {
      const next = ascending[i + 1];
      return {
        rate,
        from: rate.effectiveFrom,
        to: next ? dayBefore(next.effectiveFrom) : undefined,
        state:
          i === currentIndex
            ? "current"
            : i > currentIndex
              ? "upcoming"
              : "past",
      };
    })
    .reverse();
}

/** The next 1 July on or after `today` — the start of the next financial year. */
export function nextFinancialYearStart(today: string): string {
  const year = Number(today.slice(0, 4));
  const thisYears = `${year}-07-01`;
  return today <= thisYears ? thisYears : `${year + 1}-07-01`;
}

/** "FY27" for the Australian financial year a date falls in (1 Jul to 30 Jun). */
export function financialYearLabel(iso: string): string {
  const year = Number(iso.slice(0, 4));
  const endsIn = iso.slice(5) >= "07-01" ? year + 1 : year;
  return `FY${String(endsIn).slice(2)}`;
}

/** How a charged rate was arrived at, when it is not simply the grade rate. */
export interface RateWorking {
  baseHourlyRate?: number;
  rateSource?: "grade" | "override";
  rateCardName?: string;
  multiplier?: number;
  ratesAsAt?: string;
}

/** True when there is something to explain beyond "their grade's rate". */
export function hasRateWorking(working: RateWorking): boolean {
  return (
    working.rateCardName !== undefined ||
    working.ratesAsAt !== undefined ||
    working.rateSource === "override"
  );
}

/**
 * The parts of a rate's explanation, in reading order:
 * ["Bank and receivership (25% discount on $650/hr)", "rates as at 30 Jun
 * 2024", "individual rate"]. Empty for a plain grade rate on Standard.
 */
export function describeRateWorking(working: RateWorking): string[] {
  const card =
    working.rateCardName !== undefined && working.multiplier !== undefined
      ? [
          working.baseHourlyRate !== undefined
            ? `${working.rateCardName} (${describeMultiplier(working.multiplier)} on ${formatRate(working.baseHourlyRate)})`
            : `${working.rateCardName} (${describeMultiplier(working.multiplier)})`,
        ]
      : [];
  const freeze = working.ratesAsAt
    ? [`rates as at ${formatIsoDate(working.ratesAsAt)}`]
    : [];
  const override = working.rateSource === "override" ? ["individual rate"] : [];
  return [...card, ...freeze, ...override];
}
